import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import api from '../lib/api';
import { fmtMoney, fmtNumber, STATUS_LABELS } from '../lib/format';

const PortalHeader = ({ brand }) => (
  <header className="h-14 border-b border-border bg-card/40 backdrop-blur flex items-center px-5 gap-2.5">
    <img src="/assets/logo.png" alt="Logotipo" className="h-9 w-auto" data-testid="portal-logo" />
    <div className="leading-tight">
      <div className="font-semibold text-[15px]" style={{ fontFamily: 'Space Grotesk' }}>{brand || 'CotizaBuilder'}</div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Portal de cliente · Solo lectura</div>
    </div>
    <div className="ml-auto flex items-center gap-1.5 text-[12px] text-muted-foreground">
      <ShieldCheck size={14} className="text-primary" /> Vista segura
    </div>
  </header>
);

const TotalsCard = ({ data }) => {
  const t = data.totals || {};
  const moneda = data.moneda || 'MXN';
  const gc = t.global_cascade;
  return (
    <Card className="border-border shadow-[var(--shadow-elev-1)]" data-testid="portal-totals-card">
      <CardHeader className="pb-2"><CardTitle className="text-base">Resumen de totales</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">Subtotal conceptos</span><span className="money">{fmtMoney(t.subtotal, moneda)}</span></div>
        {gc && (
          <>
            <div className="flex justify-between" data-testid="portal-global-ind"><span className="text-muted-foreground">+ Indirectos ({fmtNumber(data.global_indirect_pct, 2)}%)</span><span className="money">{fmtMoney(gc.indirect_amount, moneda)}</span></div>
            <div className="flex justify-between" data-testid="portal-global-fin"><span className="text-muted-foreground">+ Financiamiento ({fmtNumber(data.global_financing_pct, 2)}%)</span><span className="money">{fmtMoney(gc.financing_amount, moneda)}</span></div>
            <div className="flex justify-between" data-testid="portal-global-uti"><span className="text-muted-foreground">+ Utilidad ({fmtNumber(data.global_utility_pct, 2)}%)</span><span className="money">{fmtMoney(gc.utility_amount, moneda)}</span></div>
          </>
        )}
        {t.subtotal_before_iva !== undefined && t.subtotal_before_iva !== t.subtotal && (
          <div className="flex justify-between font-medium"><span className="text-muted-foreground">Subtotal antes de IVA</span><span className="money">{fmtMoney(t.subtotal_before_iva, moneda)}</span></div>
        )}
        <div className="flex justify-between"><span className="text-muted-foreground">IVA ({fmtNumber(data.iva_percentage, 0)}%)</span><span className="money">{fmtMoney(t.iva_amount, moneda)}</span></div>
        <Separator />
        <div className="flex justify-between text-base font-semibold"><span>Total</span><span className="money text-primary" data-testid="portal-total">{fmtMoney(t.total, moneda)}</span></div>
        {gc && (
          <p className="text-[11px] text-muted-foreground pt-1">
            Los precios unitarios de los conceptos no incluyen los porcentajes globales; estos se aplican sobre el subtotal como se desglosa arriba.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default function PortalPage() {
  const { token } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['portal', token],
    queryFn: async () => (await api.get(`/portal/${token}`)).data,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <PortalHeader />
        <div className="max-w-4xl mx-auto p-5 space-y-4"><Skeleton className="h-24" /><Skeleton className="h-72" /></div>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen bg-background">
        <PortalHeader />
        <div className="max-w-lg mx-auto mt-24 text-center space-y-2" data-testid="portal-invalid">
          <h1 className="text-xl font-semibold">Enlace no válido</h1>
          <p className="text-sm text-muted-foreground">Este enlace de cotización no existe o ha expirado. Solicita uno nuevo a tu asesor.</p>
        </div>
      </div>
    );
  }

  const q = data.data;
  const moneda = q.moneda || 'MXN';

  return (
    <div className="min-h-screen bg-background" data-testid="portal-page">
      <PortalHeader brand={data.brand} />
      <main className="max-w-5xl mx-auto p-4 md:p-6 space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-[13px] text-muted-foreground" data-testid="portal-quotation-number">{q.quotation_number}</span>
              <Badge variant="secondary" className="bg-muted text-muted-foreground border border-border">{STATUS_LABELS[q.status] || q.status}</Badge>
            </div>
            <h1 className="text-2xl font-semibold" data-testid="portal-quotation-name">{q.nombre}</h1>
            <p className="text-sm text-muted-foreground">Cliente: {q.cliente_nombre || '—'} · Moneda: {moneda}{moneda === 'USD' ? ` · TC ${fmtNumber(q.exchange_rate, 4)} MXN/USD` : ''}</p>
          </div>
        </div>

        {data.kind === 'apu' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-4">
              {(q.groups || []).map((g) => (
                <Card key={g.id} className="border-border shadow-[var(--shadow-elev-1)]" data-testid={`portal-group-${g.id}`}>
                  <CardHeader className="pb-2"><CardTitle className="text-[15px]">{g.nombre_grupo}</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead className="th-label">No</TableHead>
                            <TableHead className="th-label">Descripción</TableHead>
                            <TableHead className="th-label">Unidad</TableHead>
                            <TableHead className="th-label text-right">Cantidad</TableHead>
                            <TableHead className="th-label text-right">P. unitario</TableHead>
                            <TableHead className="th-label text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(g.concepts || []).map((c) => (
                            <TableRow key={c.id} className="hover:bg-background/40">
                              <TableCell className="font-mono text-[12px]">{c.numero_concepto}</TableCell>
                              <TableCell className="text-[13px] max-w-[280px]">{c.descripcion}</TableCell>
                              <TableCell className="text-muted-foreground text-[13px]">{c.unidad}</TableCell>
                              <TableCell className="money">{fmtNumber(c.cantidad)}</TableCell>
                              <TableCell className="money">{fmtMoney(c.calculated_unit_price, moneda)}</TableCell>
                              <TableCell className="money">{fmtMoney(c.calculated_total, moneda)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="h-fit lg:sticky lg:top-5"><TotalsCard data={q} /></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Card className="lg:col-span-2 border-border shadow-[var(--shadow-elev-1)]">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table data-testid="portal-container-items">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="th-label">Descripción</TableHead>
                        <TableHead className="th-label">Tipo</TableHead>
                        <TableHead className="th-label text-right">Cantidad</TableHead>
                        <TableHead className="th-label text-right">Precio unit.</TableHead>
                        <TableHead className="th-label text-right">Desc. %</TableHead>
                        <TableHead className="th-label text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(q.items || []).map((it) => (
                        <TableRow key={it.id} className="hover:bg-background/40">
                          <TableCell>
                            <div className="font-medium text-[13px]">{it.descripcion}</div>
                            <div className="text-[11px] text-muted-foreground">{[it.condicion, it.medidas].filter(Boolean).join(' · ')}</div>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-[13px]">{it.tipo}</TableCell>
                          <TableCell className="money">{fmtNumber(it.cantidad, 0)}</TableCell>
                          <TableCell className="money">{fmtMoney(it.precio_unitario, it.moneda)}</TableCell>
                          <TableCell className="money">{fmtNumber(it.descuento_pct, 1)}</TableCell>
                          <TableCell className="money">{fmtMoney(it.total, it.moneda)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
            <div className="h-fit"><TotalsCard data={q} /></div>
          </div>
        )}

        <p className="text-[12px] text-muted-foreground text-center pb-8">
          Documento informativo generado por {data.brand || 'CotizaBuilder'}. Los precios están sujetos a cambio sin previo aviso salvo cotización finalizada.
        </p>
      </main>
    </div>
  );
}
